import { AbButton } from "../button/button.widget.js";
import { uuid } from "../../utils/random-utils.js";
import { BaseComponent } from "../../base.component.js";
import { SwipeHandler } from '../../utils/dom-utils.js';

/**
 * @typedef {RenderableProps} _AbCarousel_Props
 * @property {number} shownChildIdx
 */

/**
 * @typedef {object} _AbCarousel_State
 * @property {string} compId
 * @property {number} shownChildIdx
 * @property {number} nextChildIdx
 * @property {number} prevChildIdx
 */

export class AbCarousel extends BaseComponent {

  /** @type {_AbCarousel_State} */
  state;

  /**
   * @param {_AbCarousel_Props} props
   */
  constructor(props) {
    super(props);

    const shownChildIdx = props.shownChildIdx || 0;
    this._refreshChildren(shownChildIdx, props.children.length);
  }

  /**
   * @private
   * @param {number} shownChildIdx
   * @param {number} childrenCount
   */
  _refreshChildren(shownChildIdx, childrenCount) {
    if (shownChildIdx >= childrenCount) {
      shownChildIdx = 0;
    } else if (shownChildIdx < 0) {
      shownChildIdx = childrenCount - 1;
    }

    let nextChildIdx = shownChildIdx + 1;
    if (nextChildIdx >= childrenCount) {
      nextChildIdx = 0;
    }

    let prevChildIdx = shownChildIdx - 1;
    if (prevChildIdx < 0) {
      prevChildIdx = childrenCount - 1;
    }

    this.setState({
      compId: `C${uuid()}`,
      shownChildIdx,
      nextChildIdx,
      prevChildIdx
    });
  }

  /**
   * @private
   * Shortcut for getting a carousel managed element
   * @param {'content'|'prev'|'next'|'current-shown'|'current-shadow'} type
   * @returns {HTMLElement}
   */
  _getContentElement(type) {
    return this.context.rootElement.querySelector(`#${this.state.compId}_ab-${type}`);
  }

  /**
   * @private
   * Get the distance to use between carousel elements - a factor larger than the size of the current element, so
   * prev/next are overflown.
   * @returns {number}
   */
  _getDistance() {
    return this._getContentElement('current-shadow').clientWidth * 1.3;
  }

  /**
   * Takes care of the "animation" by handling the prev/next/current items location on screen.
   * After transition ends, the component is cleaned and re-rendered with its new current item.
   * For proper display, to avoid parent size changes, the current item also has a shadow that's already placed on
   *   position absolute (just like prev/next items); during animation, this is the item that actually moves, while the
   *   shown current item remains fixed but is hidden.
   *
   * @private
   * @param {'next'|'prev'} dir
   */
  go(dir) {
    const content = this._getContentElement('content');
    const shadowItem = this._getContentElement('current-shadow');
    const shownItem = this._getContentElement('current-shown');
    const followingItem = this._getContentElement(dir);

    const dirSign = (dir === 'next' ? +1 : -1);
    const distance = this._getDistance();

    // using this class only during transition allows react correctly re-rendering (i.e. not seeing transitions due to change in children)
    content.classList.add('ab-transition');

    // shadow takes over for transition
    shownItem.style.opacity = "0";
    shadowItem.style.opacity = "1.0";

    shadowItem.style.left = `${-dirSign * distance}px`;
    followingItem.style.left = "0px";

    const onTransitionEnd = () => {
      content.classList.remove('ab-transition');
      shownItem.style.opacity = "1.0";
      shadowItem.style.opacity = "0";
      followingItem.removeEventListener('transitionend', onTransitionEnd);
      this._refreshChildren(this.state.shownChildIdx + dirSign, this.props.children.length);
    };
    followingItem.addEventListener('transitionend', onTransitionEnd);
  }

  componentDidMount() {
    // using an immediate timeout so that the elements have time to "have width" before refreshing (seems to be sufficient)
    setTimeout(() => this._refreshElements());
    if (this.ref) {
      (this.swipeHandler = new SwipeHandler(this.ref))
        .onLeft(this.go.bind(this, "next")).onRight(this.go.bind(this, "prev")).run();
    }
  }

  componentDidUpdate() {
    this._refreshElements();
  }

  componentWillUnmount() {
    this.swipeHandler && this.swipeHandler.dispose();
  }

  _refreshElements() {
    const shadowItem = this._getContentElement('current-shadow');
    const prevItem = this._getContentElement('prev');
    const nextItem = this._getContentElement('next');

    if (shadowItem && nextItem && prevItem) {
      const distance = this._getDistance();
      shadowItem.style.left = "0px";
      nextItem.style.left = `${distance}px`;
      prevItem.style.left = `-${distance}px`;
    }
  }

  setRef(ref) {
    this.ref = ref;
  }

  /**
   * @param {_AbCarousel_Props} props
   * @param {_AbCarousel_State} state
   * @returns {ComponentChild}
   */
  render(props, state, ctx) {
    const { children } = props;
    if (children.length >= 2) {
      return (
        <div className="ab-carousel" ref={this.setRef.bind(this)}>
          <AbButton type="icon" icon="arrow.svg" iconFlip={true} onClick={() => this.go('prev')} />
          <div className="ab-content" id={`${state.compId}_ab-content`}>
            <div className="ab-content-item ab-prev-item ab-behind" id={`${state.compId}_ab-prev`}>
              {children[state.prevChildIdx]}
            </div>
            <div className="ab-content-item ab-current-item-shadow ab-behind" id={`${state.compId}_ab-current-shadow`}>
              {children[state.shownChildIdx]}
            </div>
            <div className="ab-content-item ab-current-item-shown" id={`${state.compId}_ab-current-shown`} style={`z-index: ${ctx.zIndex + 1};`}>
              {children[state.shownChildIdx]}
            </div>
            <div className="ab-content-item ab-next-item ab-behind" id={`${state.compId}_ab-next`}>
              {children[state.nextChildIdx]}
            </div>
          </div>
          <AbButton type="icon" icon="arrow.svg" iconFlip={false} onClick={() => this.go('next')} />
        </div>
      );
    } else {
      // single-content carousel => simple display of the current item
      return (
        <div className="ab-carousel">
          <div className="ab-content" id={`${state.compId}_ab-content`}>
            <div className="ab-content-item ab-current-item-shown" id={`${state.compId}_ab-current-shown`}>
              {children[state.shownChildIdx]}
            </div>
          </div>
        </div>
      );
    }
  }

}
